"use client";
import {
  Button,
  Label,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { Link20Regular } from "@fluentui/react-icons";
import { getMapExtensionToIconType, getOpenLink } from "@skanska/vsa-file-icon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CreateDraftImageButton } from "../components/image/CreateDraftImageButton";
import { ImageHistoryDialog } from "../components/image/ImageHistoryDialog";
import { OldImageVersionMessageBar } from "../components/image/OldImageVersionMessageBar";
import ActivitiesUsingToolTable from "../components/tool/ActivitiesUsingToolTable";
import { useGetPublished } from "../hooks/useImages";
import { getMediaFileTypeDisplay, isImageFile } from "../services/ImageService";
import { missingImagePlaceholder } from "../services/MissingImagePlaceholder";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  wrapper: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "1000px",
  },
  labels: {
    fontWeight: "bold",
  },
  fields: {
    marginTop: "16px",
    display: "grid",
    rowGap: "12px",
  },
  buttons: {
    display: "flex",
    gap: "10px",
    padding: tokens.spacingVerticalM,
  },
  notFound: {
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  imagePreview: {
    maxWidth: "400px",
    maxHeight: "300px",
    objectFit: "contain",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
  },
  imagePreviewHidden: {
    display: "none",
  },
  imageContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-start",
    marginTop: "8px",
  },
  imageLink: {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    alignItems: "center",
    alignContent: "center",
  },
});

const ImageOverviewPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const { data: publishedImage, isLoading } = useGetPublished(id || "");

  useEffect(() => {
    if (publishedImage?.webUrl) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setImageLoading(true);
    }
  }, [publishedImage?.webUrl]);

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.wrapper}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (!publishedImage) {
    return (
      <div className={classes.root}>
        <div className={classes.wrapper}>
          <div className={classes.notFound}>{t("imageNotFound")}</div>
        </div>
      </div>
    );
  }

  const resolvedFileType = getMediaFileTypeDisplay(publishedImage);
  const fileExtensionType = getMapExtensionToIconType(
    resolvedFileType || publishedImage.fileType
  );

  return (
    <div className={classes.root}>
      {publishedImage &&
        !publishedImage.isCurrentVersion &&
        publishedImage.imageId && (
          <OldImageVersionMessageBar imageId={publishedImage.imageId} />
        )}

      <div className={classes.wrapper}>
        <div className={classes.content}>
          <h2>{publishedImage.title}</h2>

          <div className={classes.fields}>
            <Label className={classes.labels}>{t("title")}</Label>
            {publishedImage.title}
            <Label className={classes.labels}>{t("fileName")}</Label>
            {publishedImage.fileName}

            <Label className={classes.labels}>{t("file")}</Label>
            <div className={classes.imageContainer}>
              {publishedImage.webUrl &&
                (isImageFile(publishedImage.webUrl, publishedImage.fileName) ? (
                  <div>
                    {imageLoading && <Spinner size="medium" />}
                    <img
                      src={publishedImage.webUrl}
                      alt={publishedImage.title}
                      width={240}
                      height={180}
                      title={publishedImage.title}
                      className={`${classes.imagePreview} ${imageLoading ? classes.imagePreviewHidden : ""}`}
                      onClick={() =>
                        window.open(publishedImage.webUrl, "_blank")
                      }
                      onLoad={() => setImageLoading(false)}
                      onError={(e) => {
                        setImageLoading(false);
                        if (e.currentTarget.src !== missingImagePlaceholder) {
                          e.currentTarget.src = missingImagePlaceholder;
                        }
                      }}
                    />
                  </div>
                ) : (
                  <a
                    href={getOpenLink({
                      webUrl: publishedImage.webUrl,
                      fileType: fileExtensionType,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.imageLink}
                  >
                    <Link20Regular />
                    {publishedImage.fileName}
                  </a>
                ))}
            </div>

            <Label className={classes.labels}>{t("fileType")}</Label>
            {resolvedFileType}

            {publishedImage.fileSizeBytes && (
              <>
                <Label className={classes.labels}>{t("fileSize")}</Label>
                {publishedImage.fileSizeBytes}
              </>
            )}

            {publishedImage.pixelDimensions && (
              <>
                <Label className={classes.labels}>{t("imageDimensions")}</Label>
                {publishedImage.pixelDimensions.width} x{" "}
                {publishedImage.pixelDimensions.height} px
              </>
            )}

            <Label className={classes.labels}>{t("createdBy")}</Label>
            {publishedImage.createdBy?.displayName}

            {publishedImage.publishedBy && (
              <>
                <Label className={classes.labels}>{t("publishedBy")}</Label>
                {publishedImage.publishedBy.displayName}
              </>
            )}

            <Label className={classes.labels}>
              {t("activitiesUsingThisImage")}
            </Label>
            {publishedImage.activities &&
            publishedImage.activities.length > 0 ? (
              <ActivitiesUsingToolTable
                activities={publishedImage.activities}
              />
            ) : (
              <div>{t("noActivitiesUsingThisImage")}</div>
            )}
          </div>
        </div>
      </div>

      <div className={classes.buttons}>
        <Button onClick={() => navigate(-1)} appearance="secondary">
          {t("back")}
        </Button>
        {publishedImage && publishedImage.imageId && (
          <CreateDraftImageButton
            imageId={publishedImage.imageId!}
            versionId={publishedImage.versionId!}
          />
        )}
        {publishedImage &&
          publishedImage.isCurrentVersion &&
          publishedImage.imageId && (
            <ImageHistoryDialog imageId={publishedImage.imageId} />
          )}
      </div>
    </div>
  );
};

export default ImageOverviewPage;
